/**
 * Packaging OS — Label & Claim review page (Phase 23A).
 *
 * Calls POST /api/workflows/label-copy/dry-run. Renders the
 * deterministic 10-section response. No AI provider runs in this
 * phase — risky claims are surfaced via the deterministic taxonomy
 * and never rewritten as approved.
 */

import { useState, type FormEvent } from "react";
import {
  DRY_RUN_CHANNELS,
  DRY_RUN_LANGUAGES,
  INTAKE_OBJECTIVES,
  runLabelCopyDryRun,
  type ApiResult,
  type ClaimRiskRow,
  type DryRunChannel,
  type DryRunLanguage,
  type IntakeObjective,
  type LabelCopyDryRunRequest,
  type LabelCopyDryRunResponse,
  runWorkflowPersistedFirst,
} from "../api/client";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import {
  FieldGrid,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { RiskPill } from "../components/RiskPill";
import { AIAssistToggle } from "../components/AIAssistToggle";
import { AIAssistStatusBanner } from "../components/AIAssistStatusBanner";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  product_name: string;
  category: string;
  brand_name: string;
  target_market: string;
  target_language: DryRunLanguage;
  sales_channel: string;
  product_description: string;
  packaging_objective: IntakeObjective;
  claim_drafts: string;
  tone_of_voice: string;
  claims_to_avoid: string;
  required_label_elements: string;
}

const EMPTY: FormState = {
  product_name: "",
  category: "",
  brand_name: "",
  target_market: "",
  target_language: "en",
  sales_channel: "",
  product_description: "",
  packaging_objective: "new_packaging",
  claim_drafts: "",
  tone_of_voice: "",
  claims_to_avoid: "",
  required_label_elements: "",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<LabelCopyDryRunResponse> };

interface FieldErrors {
  product_name?: string;
  category?: string;
  brand_name?: string;
  target_market?: string;
}

function splitLines(s: string, max: number): string[] {
  return s
    .split("\n")
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
    .slice(0, max);
}

export function LabelCopyPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    target_language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
      ? locale
      : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [aiAssist, setAiAssist] = useState<boolean>(false);

  function update<K extends keyof FormState>(k: K, v: FormState[K]): void {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function validate(): FieldErrors {
    const e: FieldErrors = {};
    if (!form.product_name.trim()) e.product_name = t.workflowDryRunRequiredHint;
    if (!form.category.trim()) e.category = t.workflowDryRunRequiredHint;
    if (!form.brand_name.trim()) e.brand_name = t.workflowDryRunRequiredHint;
    if (!form.target_market.trim())
      e.target_market = t.workflowDryRunRequiredHint;
    return e;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const body: LabelCopyDryRunRequest = {
      product_name: form.product_name.trim(),
      category: form.category.trim(),
      brand_name: form.brand_name.trim(),
      target_market: form.target_market.trim().toUpperCase(),
      target_language: form.target_language,
      sales_channel: form.sales_channel
        ? (form.sales_channel as DryRunChannel)
        : null,
      product_description: form.product_description.trim() || null,
      packaging_objective: form.packaging_objective,
      claim_drafts: splitLines(form.claim_drafts, 32),
      tone_of_voice: form.tone_of_voice.trim() || null,
      claims_to_avoid: splitLines(form.claims_to_avoid, 32),
      required_label_elements: splitLines(form.required_label_elements, 12),
      ai_assist: aiAssist,
    };

    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("label-copy", body, runLabelCopyDryRun);
    setState({ kind: "loaded", result });
  }

  function onReset(): void {
    setForm({
      ...EMPTY,
      target_language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
        ? locale
        : "en") as DryRunLanguage,
    });
    setErrors({});
    setState({ kind: "idle" });
  }

  const opt = (vals: readonly string[]) =>
    vals.map((v) => ({ value: v, label: v }));

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">
          {t.appShellNavWorkflows}
        </span>
        <h1 className="po-section-header__title">{t.labelCopyTitle}</h1>
        <p className="po-note">{t.labelCopySubtitle}</p>
      </header>

      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <FieldGrid>
            <TextField
              label={t.labelCopyFieldProductName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name}
              onChange={(v) => update("product_name", v)}
              error={errors.product_name}
              maxLength={200}
            />
            <TextField
              label={t.labelCopyFieldCategory}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.category}
              onChange={(v) => update("category", v)}
              error={errors.category}
              maxLength={100}
            />
            <TextField
              label={t.labelCopyFieldBrandName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.brand_name}
              onChange={(v) => update("brand_name", v)}
              error={errors.brand_name}
              maxLength={200}
            />
            <TextField
              label={t.labelCopyFieldTargetMarket}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.target_market}
              onChange={(v) => update("target_market", v.toUpperCase())}
              error={errors.target_market}
              maxLength={8}
            />
            <SelectField
              label={t.labelCopyFieldTargetLanguage}
              value={form.target_language}
              onChange={(v) => update("target_language", v as DryRunLanguage)}
              options={opt(DRY_RUN_LANGUAGES)}
            />
            <SelectField
              label={t.labelCopyFieldSalesChannel}
              value={form.sales_channel}
              onChange={(v) => update("sales_channel", v)}
              options={opt(DRY_RUN_CHANNELS)}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <SelectField
              label={t.labelCopyFieldPackagingObjective}
              value={form.packaging_objective}
              onChange={(v) =>
                update("packaging_objective", v as IntakeObjective)
              }
              options={opt(INTAKE_OBJECTIVES)}
            />
            <TextField
              label={t.labelCopyFieldToneOfVoice}
              value={form.tone_of_voice}
              onChange={(v) => update("tone_of_voice", v)}
              maxLength={500}
            />
            <TextAreaField
              label={t.labelCopyFieldProductDescription}
              value={form.product_description}
              onChange={(v) => update("product_description", v)}
              rows={3}
              maxLength={2000}
              full
            />
            <TextAreaField
              label={t.labelCopyFieldClaimDrafts}
              hint={t.labelCopyFieldClaimDraftsHelp}
              value={form.claim_drafts}
              onChange={(v) => update("claim_drafts", v)}
              rows={4}
              full
            />
            <TextAreaField
              label={t.labelCopyFieldClaimsToAvoid}
              value={form.claims_to_avoid}
              onChange={(v) => update("claims_to_avoid", v)}
              rows={2}
              full
            />
            <TextAreaField
              label={t.labelCopyFieldRequiredLabelElements}
              hint={t.labelCopyFieldRequiredLabelElementsHelp}
              value={form.required_label_elements}
              onChange={(v) => update("required_label_elements", v)}
              rows={2}
              full
            />
          </FieldGrid>

          {Object.keys(errors).length > 0 && (
            <p className="po-field__error">
              {t.workflowDryRunValidationFailed}
            </p>
          )}

          <AIAssistToggle
            checked={aiAssist}
            onChange={setAiAssist}
            label={t.aiAssistToggleLabel}
            hint={t.aiAssistToggleHint}
            banner={t.aiAssistDraftBanner}
            disabled={state.kind === "submitting"}
          />

          <div className="po-row">
            <Button
              variant="primary"
              type="submit"
              disabled={state.kind === "submitting"}
            >
              {state.kind === "submitting"
                ? t.labelCopySubmitting
                : t.labelCopySubmit}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={onReset}
              disabled={state.kind === "submitting"}
            >
              {t.labelCopyReset}
            </Button>
          </div>
        </form>
      </section>

      {state.kind === "loaded" && (
        <DataState
          state={{ kind: "loaded", result: state.result }}
          t={t}
          renderOk={(data, requestId) => (
            <LabelCopyReport data={data} requestId={requestId} t={t} />
          )}
        />
      )}
    </AppLayout>
  );
}

interface LabelCopyReportProps {
  data: LabelCopyDryRunResponse;
  requestId: string | null;
  t: UiStrings;
}

function LabelCopyNextVisualLink({ t }: { t: UiStrings }): JSX.Element {
  const [locale] = useLocale();
  const target = `/${locale}/app/workflows/visual-direction`;
  return (
    <p className="po-row">
      <a
        href={target}
        className="po-btn po-btn--ghost"
        data-testid="po-label-copy-next-visual"
      >
        {t.labelCopyNextVisual} →
      </a>
    </p>
  );
}

function NextSustLink({
  t,
  rows,
}: {
  t: UiStrings;
  rows: ClaimRiskRow[];
}): JSX.Element | null {
  const [locale] = useLocale();
  const trigger = rows.some((r) =>
    [
      "sustainability_eco",
      "organic_natural",
      "origin_export",
      "performance_exaggerated",
    ].includes(r.category),
  );
  if (!trigger) return null;
  const target = `/${locale}/app/workflows/sustainability-risk`;
  return (
    <p className="po-row">
      <a
        href={target}
        className="po-btn po-btn--ghost"
        data-testid="po-label-copy-next-sust"
      >
        {t.labelCopyNextSust} →
      </a>
    </p>
  );
}

function severityToRiskLevel(
  s: ClaimRiskRow["severity"],
): "low" | "medium" | "high" {
  if (s === "high" || s === "review_required") return "high";
  if (s === "medium") return "medium";
  return "low";
}

function severityLabel(t: UiStrings, s: ClaimRiskRow["severity"]): string {
  if (s === "high" || s === "review_required") return t.reportRiskHigh;
  if (s === "medium") return t.reportRiskMedium;
  return t.reportRiskLow;
}

function LabelCopyReport({
  data,
  requestId,
  t,
}: LabelCopyReportProps): JSX.Element {
  const hasHigh = data.claim_risk_table.some(
    (r) => r.severity === "high" || r.severity === "review_required",
  );
  const hasMedium = data.claim_risk_table.some(
    (r) => r.severity === "medium",
  );
  return (
    <div className="po-stack" data-testid="po-label-copy-report">
      {hasHigh && (
        <div className="po-halt-banner po-halt-banner--high" role="alert">
          {t.reportHaltBannerHigh}
        </div>
      )}
      {!hasHigh && hasMedium && (
        <div className="po-halt-banner po-halt-banner--medium" role="alert">
          {t.reportHaltBannerMedium}
        </div>
      )}

      <AIAssistStatusBanner
        status={data.ai_assist_status}
        banner={t.aiAssistDraftBanner}
        fallbackText={t.aiAssistStatusFallback}
        offText={t.aiAssistStatusOff}
        auditHeader={t.aiAssistAuditHeader}
        audit={data.ai_audit}
      />

      {data.ai_assist_status === "ok" &&
      data.ai_safer_wording_alts &&
      data.ai_safer_wording_alts.length > 0 ? (
        <ReportSection title={t.aiAssistAuditHeader}>
          <BulletList items={data.ai_safer_wording_alts} />
        </ReportSection>
      ) : null}

      <ReportSection title={t.labelCopyReportFront}>
        <BulletList items={data.front_label_draft} />
      </ReportSection>
      <ReportSection title={t.labelCopyReportBack}>
        <BulletList items={data.back_label_draft} />
      </ReportSection>
      <ReportSection title={t.labelCopyReportStory}>
        <BulletList items={data.short_product_story} />
      </ReportSection>
      <ReportSection title={t.labelCopyReportInstructions}>
        <BulletList items={data.usage_instructions} />
      </ReportSection>
      <ReportSection title={t.labelCopyReportWarning}>
        <BulletList items={data.warning_caution_draft} />
      </ReportSection>

      <ReportSection title={t.labelCopyReportClaimRiskTable}>
        {data.claim_risk_table.length === 0 ? (
          <p className="po-note po-note--muted">{t.reportNoFlags}</p>
        ) : (
          <div className="po-table-wrap">
            <table className="po-table">
              <thead>
                <tr>
                  <th scope="col">{t.labelCopyClaimColClaim}</th>
                  <th scope="col">{t.labelCopyClaimColSeverity}</th>
                  <th scope="col">{t.labelCopyClaimColCategory}</th>
                  <th scope="col">{t.labelCopyClaimColMatched}</th>
                  <th scope="col">{t.labelCopyClaimColReview}</th>
                </tr>
              </thead>
              <tbody>
                {data.claim_risk_table.map((row, i) => (
                  <tr key={`${i}-${row.claim.slice(0, 20)}`}>
                    <td>{row.claim}</td>
                    <td>
                      <RiskPill
                        level={severityToRiskLevel(row.severity)}
                        label={severityLabel(t, row.severity)}
                      />
                    </td>
                    <td>
                      <span className="dir-ltr-island">{row.category}</span>
                    </td>
                    <td>
                      <span className="dir-ltr-island">
                        {row.matched_terms.join(", ") || "—"}
                      </span>
                    </td>
                    <td>
                      {row.review_required
                        ? t.reportReviewRequiredYes
                        : t.reportReviewRequiredNo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ReportSection>

      <ReportSection title={t.labelCopyReportSaferWording}>
        <BulletList
          items={data.safer_wording_suggestions}
          empty={t.reportNoFlags}
        />
        <NextSustLink t={t} rows={data.claim_risk_table} />
      </ReportSection>
      <ReportSection title={t.labelCopyReportMissingInfo}>
        <BulletList items={data.missing_label_information} />
      </ReportSection>
      <ReportSection title={t.labelCopyReportHumanReview}>
        <BulletList
          items={data.human_review_required_notes}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.labelCopyReportCopyQuality}>
        <BulletList items={data.copy_quality_notes} />
        <LabelCopyNextVisualLink t={t} />
      </ReportSection>

      <ReportSection title={t.reportTrustNotes}>
        <dl className="po-dl">
          <dt>{t.trustNotesSourceBacked}</dt>
          <dd>
            {data.trust_notes.source_backed
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.trustNotesRegulatoryGuarantee}</dt>
          <dd>
            {data.trust_notes.regulatory_guarantee
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.trustNotesFinalOutput}</dt>
          <dd>
            {data.trust_notes.final_output
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.reportClaimRiskReviewRequired}</dt>
          <dd>
            {data.trust_notes.human_review_required
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
        </dl>
      </ReportSection>
      <ReportSection title={t.reportNextSteps}>
        <BulletList items={data.next_steps} />
      </ReportSection>

      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
